import { HttpMethod } from '../utils/enums/http-method.enum';
import { RequestClientInterfaceService } from './request-client.interface.service';
import { AxiosResponse, ResponseType } from 'axios';
import {
    COGNITO_TOKEN_NAME,
    DEALER_PORTAL_URL,
    PERMISSIONS_TOKEN_NAME,
} from '../config';
import {
    CreditApplicationStatus,
    CreditSearchTypes,
    CreditStatusTableData,
    ICreditStatusObject,
    ISaveQuickFundRequest,
} from './models/credit.service';

/**
 * Class responsible for manage the API calls and data request of the App
 */
export class CreditAppService {
    private readonly dealerPortalUrlStructure = `${DEALER_PORTAL_URL}`;
    private client: RequestClientInterfaceService;
    private headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: '',
    };
    /**
     * Instantiates a new ClientService
     */
    constructor() {
        this.client = new RequestClientInterfaceService();
    }

    /**
     * Retrieves the vehicle makes associated with dealer
     * @param dealerId DealerId for dealers makes
     */
    public async getMakes(
        dealerId: string,
        brand: string,
        country: string,
        vehicleType: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getMakes`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                jwtToken: permissionsToken,
                brand,
                country,
                vehicleType,
            },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the vehicle makes information associated with dealer
     * @param dealerId DealerId for dealers makes
     * @param make Make: VOLKSWAGEN, etc
     * @param brand Brand: Audi, VW, etc
     * @param country Dealer country: Canada, USA, etc
     * @param vehicleType Information about vehicle: New, etc
     */
    public async getMakesInfo(
        dealerId: string,
        make: string,
        brand: string,
        country: string,
        vehicleType: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getMakeInfo`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                jwtToken: permissionsToken,
                make,
                brand,
                country,
                vehicleType,
            },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves if dealer is allowed to finance retail, balloon and isOMSC
     * @param dealerId
     * @returns
     */
    public async getDealerAllowedToFinance(
        dealerId: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealerAllowedToFinance`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Sends Credit Application Submittion
     * @param crediAppData Object with all information for Credit Application
     */
    public async submitApplicaton(crediAppData: any): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/submitCreditApp`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                jwtToken: permissionsToken,
                ...crediAppData,
            },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the credit widget data
     * @param dealerId DealerId for funding widget
     */
    public async creditStatusWidget(dealerId: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/creditWidget`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { dealerId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the credit search data
     * @param dealerId DealerId
     * @param searchType
     * @param searchValue
     */
    public async creditStatusSearch(
        dealerId: string,
        searchType: CreditSearchTypes,
        searchValue: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/creditSearch`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                searchType,
                searchValue,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public mapCreditStatusResponse(
        data: ICreditStatusObject[],
    ): CreditStatusTableData[] {
        return data.map((item) => {
            const name = item.businessName
                ? item.businessName
                : `${item.applicantFirstName} ${item.applicantLastName}`;
            return {
                key: item.id,
                name,
                applicationNumber: item.applicationNumber,
                status: item.status as CreditApplicationStatus,
                collateral: item.vehicleDescription,
                submittedBy: item.submittedBy,
                submittedTime: item.submittedTime,
                sourceSystem: item.sourceSystem,
            };
        });
    }

    /**
     * Retrieves the credit search data
     * @param dealerId DealerId
     * @param applicationId
     */
    public async creditDetails(
        dealerId: string,
        applicationId: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/creditDetails`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                applicationId,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the credit search data
     * @param dealerId DealerId
     * @param applicationId
     */
    public async creditApplicationPDF(
        requestObject: any,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getCreditApplicationPDF`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                ...requestObject,
                jwtToken: permissionsToken,
            },
            responseType: 'blob' as ResponseType,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Sends Quick Fund Submittion
     * @param saveQuickFundRequest Object with all information for Quick Fund
     */
    public async submitQuickFund(
        saveQuickFundRequest: ISaveQuickFundRequest,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/saveQuickFund`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                jwtToken: permissionsToken,
                ...saveQuickFundRequest,
            },
        };
        return this.client.sendRequest(request);
    }

    public abort() {
        this.client.controller.abort();
    }
}
